// pages/brand/BrandPage.jsx (or pages/Brand.jsx)
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSEO } from "../../hooks/useSEO";
import {
  fetchModelProducts,
  clearModelData,
  setSelectedSeriesId,
  clearSelectedSeriesId,
} from "../../store/slices/modelSlice";
import { useTheme } from "../../theme/ThemeProvider";
import Layout from "../../components/Layout";
import ModelGrid from "../../components/model/ModelGrid";
import ModelSkeleton from "../../components/model/ModelSkeleton";
import CategoryHeader from "../../components/category/CategoryHeader";
import ErrorState from "../../components/state/ErrorState";
import EmptyState from "../../components/state/EmptyState";
import SeriesFilter from "../../components/model/SeriesFilter";

const BrandPage = () => {
  const dispatch = useDispatch();
  const { categorySlug, brandSlug } = useParams();
  const { isDarkMode } = useTheme();

  // Get city from Redux
  const { selectedCity } = useSelector((state) => state.city);
  const cityName = selectedCity?.name || "";

  const {
    loading,
    error,
    category: categoryData,
    brand,
    series,
    products,
    selectedSeriesId: selectedId,
  } = useSelector((state) => state.model);

  const [selectedSeriesId, setSelectedSeriesIdState] = useState("");

  const seriesList = useMemo(() => series || [], [series]);
  const productList = useMemo(() => products || [], [products]);
  const categoryName = categoryData?.name || "";
  const brandName = brand?.name || "";

  useEffect(() => {
    if (selectedId) setSelectedSeriesIdState(selectedId);
  }, [selectedId]);

  // ============= SEO - Fixed undefined issues =============
  const seoParams = useMemo(() => {
    if (loading || !brand) return null;

    return {
      categorySlug: categorySlug,
      brandSlug: brandSlug,
      categoryName: categoryData?.name || "",
      brandName: brand?.name || "",
      brandDescription: brand?.description || "",
      productCount: productList?.length || 0,
    };
  }, [loading, brand, categorySlug, brandSlug, categoryData, productList]);

  useSEO(`/sell-used-online/${categorySlug}/${brandSlug}`, seoParams);

  // Fetch when category, brand, OR city changes
  useEffect(() => {
    if (categorySlug && brandSlug) {
      // Clear old data when city changes
      dispatch(clearModelData());
      dispatch(
        fetchModelProducts({
          category: categorySlug,
          model: brandSlug,
          selectedSeriesId,
        }),
      );
    }
    return () => {
      dispatch(clearModelData());
      dispatch(clearSelectedSeriesId());
    };
  }, [dispatch, categorySlug, brandSlug, selectedSeriesId, selectedCity?.id]);

  const handleSeriesChange = useCallback(
    (seriesId) => {
      setSelectedSeriesIdState(seriesId);
      dispatch(setSelectedSeriesId(seriesId));
    },
    [dispatch],
  );

  const filteredProducts = useMemo(() => {
    if (!selectedSeriesId) return productList;
    return productList.filter(
      (product) => product.series_id === parseInt(selectedSeriesId, 10),
    );
  }, [productList, selectedSeriesId]);

  const handleRetry = useCallback(() => {
    dispatch(
      fetchModelProducts({
        category: categorySlug,
        model: brandSlug,
        selectedSeriesId,
      }),
    );
  }, [dispatch, categorySlug, brandSlug, selectedSeriesId]);

  if (loading) {
    return (
      <Layout>
        <div className="container px-4 py-6 mx-auto max-w-7xl">
          <ModelSkeleton isDarkMode={isDarkMode} />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container px-4 py-6 mx-auto max-w-7xl">
          <ErrorState
            error={error}
            onRetry={handleRetry}
            isDarkMode={isDarkMode}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container px-4 py-6 mx-auto max-w-7xl">
        <CategoryHeader
          title={`Sell Your Old ${brandName} ${categoryName}`}
          subtitle={`Select your ${brandName} model to get the best price`}
          isDarkMode={isDarkMode}
          image={brand?.image}
        />

        <SeriesFilter
          seriesList={seriesList}
          selectedSeriesId={selectedSeriesId}
          onSeriesChange={handleSeriesChange}
          isDarkMode={isDarkMode}
          productCount={filteredProducts.length}
        />

        {filteredProducts.length === 0 ? (
          <EmptyState
            isDarkMode={isDarkMode}
            title="No products available"
            message={`We couldn't find any products for ${brandName}${selectedSeriesId ? ` in this series` : ""}`}
          />
        ) : (
          <ModelGrid
            brand={brand}
            products={filteredProducts}
            category={categoryData}
            city={cityName}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </Layout>
  );
};

export default BrandPage;